import React, { useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import Tippy from '@tippyjs/react';
import { sticky } from 'tippy.js'; // Import the sticky plugin
import { motion } from 'framer-motion';
import 'tippy.js/dist/tippy.css';

/**
 * Formats employment numbers (thousands -> full #).
 */
function formatEmployment(val) {
  const actualVal = val * 1000;
  if (Math.abs(actualVal) >= 1000000) {
    return (actualVal / 1000000).toFixed(1) + 'M';
  }
  if (Math.abs(actualVal) >= 1000) {
    return (actualVal / 1000).toFixed(1) + 'K';
  }
  return Math.round(actualVal).toString();
}

const TrendChartNivo = ({ data, metricMode }) => {
  const [hoveredX, setHoveredX] = useState(null);
  const [hoveredSliceContent, setHoveredSliceContent] = useState(null);

  // Prepare chart data
  const chartData = [
    {
      id: metricMode,
      data: data.map((val, idx) => ({
        x: 2000 + idx,
        y: val,
      })),  
    },
  ];

  let axisLeftLabel = 'Employment';
  if (metricMode === 'pctOfTotal') axisLeftLabel = '% of Total';
  if (metricMode === 'yoyGrowth') axisLeftLabel = 'YOY Growth %';
  if (metricMode === 'since2000') axisLeftLabel = 'Since 2000 % Change';

  // Overlay layer remains unchanged
  const overlayLayer = ({ yScale, innerWidth }) => {
    const [min, max] = yScale.domain();
    const baseline = yScale(0);
    const positiveY = yScale(max);
    const positiveHeight = baseline - positiveY;
    let negativeRect = null;

    if (min < 0) {
      const negativeHeight = yScale(min) - baseline;
      negativeRect = (
        <rect
          x={-5}
          y={baseline}
          width={innerWidth + 10}
          height={negativeHeight + 5}
          className="mix-blend-color"
          fill="#E54D2E"
        />
      );
    }

    return (
      <g>
        <rect
          x={-5}
          y={positiveY - 5}
          width={innerWidth + 10}
          height={positiveHeight + 5}
          className="mix-blend-overlay"
          fill="#00749E"
        />
        {negativeRect}
      </g>
    );
  };

  // Custom slice layer using xScale + margin for positioning
  const customSliceLayer = ({ slices, height, xScale, margin }) => {
    return (
      <g>
        {slices.map((slice) => {
          // For each slice, find the pixel x coord for the first point’s year
          const firstPoint = slice.points[0];
          const year = firstPoint.data.x;
          const xCoord = xScale(year);

          const tooltipContent = (
            <div className='text-slate-700 rounded-md px-2 py-2 bg-zinc-50 outline outline-zinc-100 outline-4 outline-offset-1'>
              {slice.points.map((point) => (
                <div key={point.id}>
                  <strong>Year {point.data.x}</strong>
                  <br />
                  {metricMode === 'raw'
                    ? `Employment: ${formatEmployment(point.data.y)}`
                    : `${point.data.y.toFixed(1)}%`}
                </div>
              ))}
            </div>
          );

          return (
            <rect
              key={slice.id}
              x={slice.x0}
              y={slice.y0}
              width={slice.width}
              height={slice.height}
              fill="transparent"
              onMouseEnter={() => {
                // Save an absolute x (container coordinate) that includes margin.left
                const anchorX = margin.left + xCoord;
                setHoveredX(anchorX);
                setHoveredSliceContent(tooltipContent);
              }}
              onMouseLeave={() => {
                setHoveredX(null);
                setHoveredSliceContent(null);
              }}
            />
          );
        })}
        {/* The animated crosshair line, drawn in the chart's coordinate space */}
        {hoveredX !== null && (
  <motion.line
    style={{ pointerEvents: 'none' }}
    y1={0}
    y2={height}
    stroke="#000"
    strokeWidth={1.5}
    strokeDasharray="5 5"
    initial={{
      x1: hoveredX - margin.left,
      x2: hoveredX - margin.left,
      opacity: 0,
    }}
    animate={{
      x1: hoveredX - margin.left,
      x2: hoveredX - margin.left,
      opacity: 1,
    }}
    transition={{
      type: 'spring',
      stiffness: 200,
      damping: 34,
    }}
  />
)}
      </g>
    );
  };
// 1) Define a custom points layer component:
const CustomPointsLayer = ({ points }) => {
  return (
    <g>
      {points.map(pt => {
        
        // each "pt" is a single point object
        const value = pt.data.y
        // color logic
        const fill = value >= 0 ? '#0ea5e9' : '#f97316'
        return (
          <circle
            key={pt.id}
            cx={pt.x}
            cy={pt.y}
            r={3}
            fill={"#fff"}
            stroke={fill}
            strokeWidth={3}
            // prevent the point itself from stealing hover events if you prefer
            style={{ pointerEvents: 'none' }}
          />
        )
      })}
    </g>
  )
}
  // Layers
  const customLayers = [
    'grid',
    'markers',
    'areas',
    'lines',
    
    metricMode === 'yoyGrowth' || 'since2000'  ? overlayLayer : null,
    'axes',
    customSliceLayer,CustomPointsLayer,
    'legends',
  ].filter(Boolean);

  return (
    <div className="bg-white" style={{ width: '100%', height: 300, position: 'relative' }}>
      <ResponsiveLine
        data={chartData}
        theme={{ background: 'white' }}
        xScale={{ type: 'linear', min: 2000, max: 2021 }}
        yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
        curve="monotoneX"
        enableArea={true}
        areaBaselineValue={0}
        areaOpacity={0.1}
        margin={{ top: 80, right: 20, bottom: 50, left: 60 }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickValues: [2000, 2005, 2010, 2015, 2020],
          legend: 'Year',
          legendOffset: 40,
          legendPosition: 'middle',
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          legend: axisLeftLabel,
          legendOffset: -50,
          legendPosition: 'middle',
          format: metricMode === 'raw' ? formatEmployment : undefined,
        }}
        colors="#3498db"
        enableGridX={false}
        enableGridY={false}
        useMesh={false}
        crosshairType="cross"
        enableSlices="x"
        layers={customLayers}
      />
     {hoveredX !== null && hoveredSliceContent && (
  <Tippy
    theme="industryTrends"
    interactive={true} // Allows users to hover over the tooltip without it disappearing
    sticky={true}      // Keeps the tooltip "stuck" to the reference element
    plugins={[sticky]} // Include the sticky plugin
    placement="top"
    offset={[0, 0]}
    content={hoveredSliceContent}
    visible={true}
  >
    <motion.div
      animate={{ left: hoveredX }}
      transition={{
        type: 'spring',
        stiffness: 180,
        damping: 40
      }} // Customize these values to control smoothness
      style={{
        position: 'absolute',
        top: 60, // Anchored at the top (or you can use margin.top dynamically)
        left: hoveredX,
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
      }}
    />
  </Tippy>
      )}
    </div>
  );
};

export default TrendChartNivo;